import { isBugBountyParticipant } from '@atlassian/amkt-common';
import { ANALYTICS_SUBJECT_ID_DISCOVER_TILE, ErrorStateEvent, LinkClickedEvent, TileClickedEvent, TilesRenderedEvent, useAnalyticsEvents } from '@atlassian/mpac-analytics';
import { breakpointValues, mapResponsiveBreakpointToScreenSize, useResponsiveBreakpoint } from '@atlassian/mpac-primitives';
import { CloudFortifiedStatusValues } from '@atlassian/mpac-types';
import { AtlassianProductHostingType } from '@atlassian/mpac-types/graphql-gateway-types';
import { ANALYTICS_SUBJECT_ID_COLLECTION_VIEW_MORE_LINK, ANALYTICS_SUBJECT_ID_HIT, ANALYTICS_SUBJECT_ID_HOME_PAGE_TILES, LARGE, SMALL } from './constants';
export const isAppCloudSecurityParticipant = app => {
  var _app$programs;
  return isBugBountyParticipant(AtlassianProductHostingType.Cloud, app === null || app === void 0 ? void 0 : (_app$programs = app.programs) === null || _app$programs === void 0 ? void 0 : _app$programs.bugBountyParticipant) && app.deployments.includes('cloud');
};
export const isAppCloudFortified = app => {
  var _app$programs2, _app$programs2$cloudF;
  return ((_app$programs2 = app.programs) === null || _app$programs2 === void 0 ? void 0 : (_app$programs2$cloudF = _app$programs2.cloudFortified) === null || _app$programs2$cloudF === void 0 ? void 0 : _app$programs2$cloudF.status) === CloudFortifiedStatusValues.Approved;
};
export const isRunsOnAtlassian = app => {
  var _app$programs3, _app$programs3$runsOn;
  return (_app$programs3 = app.programs) === null || _app$programs3 === void 0 ? void 0 : (_app$programs3$runsOn = _app$programs3.runsOnAtlassian) === null || _app$programs3$runsOn === void 0 ? void 0 : _app$programs3$runsOn.status;
};
export const isEcosystemCloudApp = app => {
  var _app$version, _app$version2, _app$version2$cloud;
  return (app === null || app === void 0 ? void 0 : (_app$version = app.version) === null || _app$version === void 0 ? void 0 : _app$version.cloud) && (app === null || app === void 0 ? void 0 : (_app$version2 = app.version) === null || _app$version2 === void 0 ? void 0 : (_app$version2$cloud = _app$version2.cloud) === null || _app$version2$cloud === void 0 ? void 0 : _app$version2$cloud.cloudAppId);
};
export const getTotalTilesWithCloudSecurityBadge = appList => {
  return appList.reduce((acc, curr) => {
    if (isAppCloudSecurityParticipant(curr)) acc += 1;
    return acc;
  }, 0);
};
export const getTotalTilesWithCloudFortifiedBadge = appList => {
  return appList.reduce((acc, curr) => {
    if (isAppCloudFortified(curr)) acc += 1;
    return acc;
  }, 0);
};
export const getTotalEcosystemCloudAppTiles = appList => {
  return appList.reduce((acc, curr) => {
    if (isEcosystemCloudApp(curr)) acc += 1;
    return acc;
  }, 0);
};
const getTotalApps = (enterpriseAppsSectionData, sectionsData, breakpoint) => {
  const isMobile = breakpoint < breakpointValues.sm;
  const isTablet = breakpoint < breakpointValues.md && breakpoint >= breakpointValues.sm;
  let totalApps = [];
  if (enterpriseAppsSectionData) {
    if (isMobile) {
      totalApps = enterpriseAppsSectionData.apps.slice(0, 3);
    } else if (isTablet) {
      totalApps = enterpriseAppsSectionData.apps.slice(0, 4);
    } else {
      totalApps = enterpriseAppsSectionData.apps;
    }
  }
  if (sectionsData) {
    sectionsData.forEach(section => {
      const breakpointId = mapResponsiveBreakpointToScreenSize(breakpoint, Object.keys(section.screenSpecificProperties));
      const {
        appsCount
      } = section.screenSpecificProperties[breakpointId];
      totalApps = [...totalApps, ...section.apps.slice(0, appsCount)];
    });
  }
  return totalApps;
};
export const useHomepageAnalytics = () => {
  const fireEvent = useAnalyticsEvents();
  const {
    value: breakpoint
  } = useResponsiveBreakpoint();
  const trackTileRenders = (enterpriseAppsSectionData, sectionsData) => {
    const totalApps = getTotalApps(enterpriseAppsSectionData, sectionsData, breakpoint);
    fireEvent({
      eventType: TilesRenderedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HOME_PAGE_TILES,
      attributes: {
        totalTiles: totalApps.length,
        appKeys: totalApps.map(app => app.addonKey),
        totalTilesWithCloudSecurityBadge: getTotalTilesWithCloudSecurityBadge(totalApps),
        totalTilesWithCloudFortifiedBadge: getTotalTilesWithCloudFortifiedBadge(totalApps),
        totalEcosystemCloudAppTiles: getTotalEcosystemCloudAppTiles(totalApps)
      }
    });
  };
  const trackTileClick = _ref => {
    var _app$version3, _app$version4, _app$version5;
    let {
      app,
      sectionTitle,
      index,
      hideDescription
    } = _ref;
    fireEvent({
      eventType: TileClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_HIT,
      attributes: {
        sectionTitle,
        label: app.addonKey,
        appName: app.name,
        hitRank: index,
        hitSize: hideDescription ? SMALL : LARGE,
        isPaidApp: app === null || app === void 0 ? void 0 : (_app$version3 = app.version) === null || _app$version3 === void 0 ? void 0 : _app$version3.paid,
        hasCloudSecurityBadge: isAppCloudSecurityParticipant(app),
        productKeys: app !== null && app !== void 0 && (_app$version4 = app.version) !== null && _app$version4 !== void 0 && _app$version4.compatibilities ? app === null || app === void 0 ? void 0 : (_app$version5 = app.version) === null || _app$version5 === void 0 ? void 0 : _app$version5.compatibilities.map(appData => appData.applicationName) : [],
        hasCloudFortifiedBadge: isAppCloudFortified(app),
        hasRunsOnAtlassianBadge: isRunsOnAtlassian(app),
        currentPerspective: 'discover'
      }
    });
  };
  const trackHomeScreenErrorState = sectionName => {
    fireEvent({
      eventType: ErrorStateEvent,
      actionSubjectId: sectionName,
      errorType: 'initial'
    });
  };
  const tractExploreMoreAppClick = (sectionTitle, slug) => {
    fireEvent({
      eventType: LinkClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_COLLECTION_VIEW_MORE_LINK,
      attributes: {
        sectionTitle,
        slug,
        label: "app:".concat(sectionTitle),
        objectId: sectionTitle
      }
    });
  };
  const trackFeaturedCollectionClick = label => {
    fireEvent({
      eventType: TileClickedEvent,
      actionSubjectId: ANALYTICS_SUBJECT_ID_DISCOVER_TILE,
      attributes: {
        label,
        tileType: LARGE,
        objectType: 'discoverTile'
      }
    });
  };
  return {
    trackTileRenders,
    trackTileClick,
    trackHomeScreenErrorState,
    tractExploreMoreAppClick,
    trackFeaturedCollectionClick,
    getTotalApps
  };
};